import {
  createElement,
  useEffect,
  useRef,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from "react";

type RevealProps = {
  children: ReactNode;
  delay?: number;
  as?: ElementType;
  className?: string;
  style?: CSSProperties;
  id?: string;
};

/**
 * Scroll-reveal wrapper — adds `.is-in` when the element enters the viewport.
 *
 * Above-the-fold elements render visible immediately (no animation) to avoid
 * stuck-invisible content before the IntersectionObserver fires on initial
 * paint. Below-the-fold elements fade + translate in as they enter view.
 */
export function Reveal({
  children,
  delay = 0,
  as = "div",
  className = "",
  ...rest
}: RevealProps) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    // Above the fold — render visible, no animation.
    if (rect.top < vh) return;

    el.classList.add("to-reveal");
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            el.style.transitionDelay = `${delay}ms`;
            el.classList.add("is-in");
            io.unobserve(el);
          }
        }
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [delay]);

  return createElement(
    as,
    { ref, className: `reveal ${className}`.trim(), ...rest },
    children
  );
}
